/**
 * Shell-wide hosts owned by modules, mounted in every mode (PLAN.md section 5):
 *
 * - `modules/phones/global/CallPopHost.tsx` (V02): the incoming-call pop on
 *   every page, not only /phones (PH-F-04, P1-37).
 * - `modules/imaging/capture/SensorPill.tsx` (V23): the capture agent /
 *   sensor status pill in the top bar's `sensors` slot (IC-14).
 * - `modules/patients/global/MedicalAlertHost.tsx`: the medical-alert popup on
 *   patient select, once per patient per session.
 * - `modules/chat/global/ChatButton.tsx` and `ChatHost.tsx` (Velora Chat):
 *   the top-bar chat icon with its unread count, and the chat drawer with
 *   its shortcut, user-menu item and desktop tray badge.
 * - `modules/rx/global/WriteRxHost.tsx`: the Write Rx drawer, opened from the
 *   patient header, the chart, Prescriptions and Ctrl+K "Write prescription".
 * - `modules/receptionist/global/ModesNavHint.tsx`: the AI Receptionist nav
 *   item's hover text (which reception modes are on).
 *
 * Contract: the file default-exports a component that takes no props and
 * renders nothing when it has nothing to show. It is found by path
 * (`import.meta.glob`), so the shell needs no change when the module lands,
 * and it loads lazily so the main chunk stays small. A host that throws is
 * contained here: a broken call pop or pill never takes the shell down.
 */
import { Component, lazy, Suspense, type ComponentType, type ReactNode } from 'react';

type HostModule = { default: ComponentType };

const CALL_POP = import.meta.glob<HostModule>('../../modules/phones/global/CallPopHost.tsx');
const SENSOR_PILL = import.meta.glob<HostModule>('../../modules/imaging/capture/SensorPill.tsx');
const ALERT_POPUP = import.meta.glob<HostModule>(
  '../../modules/patients/global/MedicalAlertHost.tsx',
);
const CHAT_BUTTON = import.meta.glob<HostModule>('../../modules/chat/global/ChatButton.tsx');
const MESSAGES_BUTTON = import.meta.glob<HostModule>(
  '../../modules/inbox/global/MessagesButton.tsx',
);
const CHAT_HOST = import.meta.glob<HostModule>('../../modules/chat/global/ChatHost.tsx');
const WRITE_RX = import.meta.glob<HostModule>('../../modules/rx/global/WriteRxHost.tsx');
const RECEPTION_MODES = import.meta.glob<HostModule>(
  '../../modules/receptionist/global/ModesNavHint.tsx',
);

/** The lazy component behind a one-file glob, or null while the file does not exist. */
export function lazyHost(glob: Record<string, () => Promise<HostModule>>): ComponentType | null {
  const load = Object.values(glob)[0];
  return load ? lazy(load) : null;
}

class Contain extends Component<{ name: string; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    // No patient data here: the host's own error, for the console only.
    console.error(`[shell] ${this.props.name} failed`, error);
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

/** One contained, lazily loaded host; nothing while `C` is null. */
export function GlobalHost({ name, C }: { name: string; C: ComponentType | null }) {
  if (!C) return null;
  return (
    <Contain name={name}>
      <Suspense fallback={null}>
        <C />
      </Suspense>
    </Contain>
  );
}

const CallPopHost = lazyHost(CALL_POP);
const SensorPill = lazyHost(SENSOR_PILL);
const MedicalAlertHost = lazyHost(ALERT_POPUP);
const ChatButton = lazyHost(CHAT_BUTTON);
const MessagesButton = lazyHost(MESSAGES_BUTTON);
const ChatHost = lazyHost(CHAT_HOST);
const WriteRxHost = lazyHost(WRITE_RX);
const ModesNavHint = lazyHost(RECEPTION_MODES);

/** The incoming-call pop, mounted once for the whole signed-in shell. */
export function GlobalCallPop() {
  return <GlobalHost name="CallPopHost" C={CallPopHost} />;
}

/** The capture status pill for the top bar's `sensors` slot (and the classic menu bar). */
export function GlobalSensorPill() {
  return <GlobalHost name="SensorPill" C={SensorPill} />;
}

/** The medical-alert popup on patient select (patients module), every mode. */
export function GlobalMedicalAlerts() {
  return <GlobalHost name="MedicalAlertHost" C={MedicalAlertHost} />;
}

/**
 * The chat icon with its unread count, before the notifications bell
 * (MODULE-AUDIT #18: team messages went unseen while Chat was only in the
 * user menu and Ctrl+K). Hidden at phone width (chat.css), where Chat stays
 * in the user menu (ChatHost).
 */
export function GlobalChatButton() {
  return <GlobalHost name="ChatButton" C={ChatButton} />;
}

/**
 * The Messages icon (patient texts) with its red unread badge, in the top
 * bar's old Velora Chat slot (W1 texting). Opens Communications.
 */
export function GlobalMessagesButton() {
  return <GlobalHost name="MessagesButton" C={MessagesButton} />;
}

/** The chat drawer and its bindings, mounted once for the whole signed-in shell. */
export function GlobalChat() {
  return <GlobalHost name="ChatHost" C={ChatHost} />;
}

/** The Write Rx drawer and its Ctrl+K command (rx module), every mode. */
export function GlobalWriteRx() {
  return <GlobalHost name="WriteRxHost" C={WriteRxHost} />;
}

/** The AI Receptionist nav item's "which modes are on" hover text. */
export function GlobalReceptionModes() {
  return <GlobalHost name="ModesNavHint" C={ModesNavHint} />;
}

/** Whether each host exists in this build (tests, the shell's own checks). */
export const GLOBAL_HOSTS = {
  callPop: CallPopHost !== null,
  sensorPill: SensorPill !== null,
  alertPopup: MedicalAlertHost !== null,
  chat: ChatHost !== null && ChatButton !== null,
  messages: MessagesButton !== null,
} as const;
